import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { fetchTrips, STATUSES, DESTINATIONS } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { DestinationBadge } from "../components/DestinationBadge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search, SlidersHorizontal, X, Calendar, Plane, Hotel, ChevronRight } from "lucide-react";
import { format, parseISO } from "date-fns";
import { cn } from "../lib/utils";

export default function AdminRecords() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ destination: "All", status: "All", search: "", in_town: false });
  const [filterOpen, setFilterOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.destination !== "All") params.destination = filters.destination;
      if (filters.status !== "All") params.status = filters.status;
      if (filters.search) params.search = filters.search;
      if (filters.in_town) params.currently_in_town = true;
      const data = await fetchTrips(params);
      setTrips(data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.destination, filters.status, filters.in_town]);

  const filteredTrips = useMemo(() => {
    if (!filters.search) return trips;
    const q = filters.search.toLowerCase();
    return trips.filter((t) =>
      t.full_name?.toLowerCase().includes(q) ||
      t.email?.toLowerCase().includes(q) ||
      t.flight_number?.toLowerCase().includes(q) ||
      t.accommodation_name?.toLowerCase().includes(q)
    );
  }, [trips, filters.search]);

  const activeFilterCount = (filters.destination !== "All" ? 1 : 0) + (filters.status !== "All" ? 1 : 0) + (filters.in_town ? 1 : 0);

  return (
    <div className="min-h-[calc(100vh-4rem)] px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-records-page">
      <div className="max-w-[1600px] mx-auto space-y-5">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="label-eyebrow mb-2">Travel Records</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white tracking-tight">All travelers</h1>
            <p className="text-sm text-zinc-400 mt-1">{filteredTrips.length} record{filteredTrips.length !== 1 ? "s" : ""} matching filters</p>
          </div>
        </div>

        {/* Search + filter bar */}
        <div className="glass-card p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Search by name, email, flight, lodging..."
                className="glass-input pl-10"
                data-testid="records-search-input"
              />
            </div>
            <Button
              variant="ghost"
              onClick={() => setFilterOpen(!filterOpen)}
              className="border border-white/10 hover:bg-white/10 text-white relative"
              data-testid="toggle-filters-button"
            >
              <SlidersHorizontal className="h-4 w-4 mr-2" /> Filters
              {activeFilterCount > 0 && (
                <span className="ml-2 bg-[#F07D00] text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>

          {filterOpen && (
            <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-1 sm:grid-cols-3 gap-4 animate-fade-up" data-testid="filters-panel">
              <div>
                <label className="label-eyebrow mb-2 block">Destination</label>
                <div className="flex gap-2 flex-wrap">
                  {["All", ...DESTINATIONS].map((d) => (
                    <button
                      key={d}
                      onClick={() => setFilters({ ...filters, destination: d })}
                      data-testid={`filter-dest-${d.toLowerCase()}`}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-xs font-semibold border transition-all",
                        filters.destination === d
                          ? "bg-[#F07D00]/20 border-[#F07D00] text-[#F07D00]"
                          : "bg-white/5 border-white/10 text-zinc-400 hover:text-white"
                      )}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="label-eyebrow mb-2 block">Status</label>
                <div className="flex gap-1.5 flex-wrap">
                  {["All", ...STATUSES].map((s) => (
                    <button
                      key={s}
                      onClick={() => setFilters({ ...filters, status: s })}
                      data-testid={`filter-status-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      className={cn(
                        "px-2.5 py-1 rounded-full text-xs font-semibold border transition-all",
                        filters.status === s
                          ? "bg-[#F07D00]/20 border-[#F07D00] text-[#F07D00]"
                          : "bg-white/5 border-white/10 text-zinc-400 hover:text-white"
                      )}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-3 flex items-center justify-between pt-1">
                <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.in_town}
                    onChange={(e) => setFilters({ ...filters, in_town: e.target.checked })}
                    className="h-4 w-4 accent-[#F07D00]"
                    data-testid="filter-in-town"
                  />
                  Currently in town only
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFilters({ destination: "All", status: "All", search: "", in_town: false })}
                  className="text-zinc-400 hover:text-white hover:bg-white/5"
                  data-testid="clear-filters-button"
                >
                  <X className="h-3 w-3 mr-1" /> Clear
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Records list */}
        {loading ? (
          <div className="text-center text-zinc-400 py-12">Loading records...</div>
        ) : filteredTrips.length === 0 ? (
          <div className="glass-card p-12 text-center" data-testid="no-records-state">
            <p className="font-heading text-lg text-white mb-2">No records found</p>
            <p className="text-sm text-zinc-400">Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="records-list">
            {filteredTrips.map((t) => (
              <Link
                key={t.id}
                to={`/admin/records/${t.id}`}
                data-testid={`record-${t.id}`}
                className="glass-card glass-card-hover block p-4 sm:p-5 group"
              >
                <div className="flex items-start sm:items-center gap-4 flex-col sm:flex-row">
                  {/* Date column */}
                  <div className="flex-shrink-0 flex sm:flex-col items-center sm:items-start gap-2 sm:gap-0 w-full sm:w-24">
                    <div className="font-heading text-xl sm:text-2xl font-semibold text-white">{format(parseISO(t.arrival_date), "MMM d")}</div>
                    <div className="text-xs text-zinc-500 uppercase tracking-wider">→ {format(parseISO(t.departure_date), "MMM d")}</div>
                  </div>

                  <div className="hidden sm:block h-12 w-px bg-white/10" />

                  {/* Main info */}
                  <div className="flex-1 min-w-0 w-full">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <h3 className="font-heading text-base font-semibold text-white truncate">{t.full_name}</h3>
                      <DestinationBadge destination={t.destination} size="sm" />
                      <StatusBadge status={t.status} />
                    </div>
                    <div className="flex items-center gap-3 text-xs text-zinc-400 flex-wrap">
                      <span className="flex items-center gap-1"><Plane className="h-3 w-3" />{t.flight_number || "TBD"}</span>
                      <span className="flex items-center gap-1"><Hotel className="h-3 w-3" />{t.accommodation_name || "Lodging TBD"}</span>
                      <span className="text-zinc-500">{t.email}</span>
                    </div>
                  </div>

                  <ChevronRight className="hidden sm:block h-5 w-5 text-zinc-600 group-hover:text-[#F07D00] group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
